'use client'

import React, { FC, ReactElement, useEffect } from 'react'
import PhoneInput, { PhoneInputProps } from 'react-phone-input-2'
import 'react-phone-input-2/lib/high-res.css'
import { useInputWrapper } from '../../../hooks/useInputWrapper'
import nl from './countries-nl'

export interface FormPhoneFieldProps extends PhoneInputProps {
    id?: string
    className?: string
    error?: boolean
    autoFocus?: boolean
    readOnly?: boolean
}

const SVG_ICONS = {
    SEARCH: `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M14.5 15.5L20 21" stroke="currentColor"/>
            <circle cx="10.5" cy="10.5" r="6" stroke="currentColor"/>
        </svg>`,
    CHECK_MARK: `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M8 13L10.5 15.5L16 9" stroke="currentColor" strokeLinecap="square"/>
        </svg>`,
}

enum CountryCode {
    NL = '31',
}

export const FormPhoneField = ({
    id,
    className = '',
    searchPlaceholder = 'Search...',
    searchNotFound = '',
    country = 'nl',
    preferredCountries = ['nl', 'be', 'de'],
    error = false,
    autoFocus = false,
    readOnly = false,
    ...rest
}: FormPhoneFieldProps): ReactElement => {
    const { onFocusHandler, onBlurHandler, classes } = useInputWrapper(false, rest.disabled)
    const htmlLocale = document.documentElement.lang

    const containerClass: string = 'form-phone-field'

    //Fix from https://github.com/bl00mber/react-phone-input-2/issues/579
    //@ts-ignore
    const ReactPhoneInput: FC<PhoneInputProps> = PhoneInput.default ? PhoneInput.default : PhoneInput

    useEffect(() => {
        setIcon()
    }, [])

    const setIcon = (): void => {
        const flagDropdown: HTMLDivElement | null = document.querySelector(`.${containerClass} .flag-dropdown`)
        if (!flagDropdown) return

        flagDropdown.addEventListener('click', (_) => {
            setTimeout(() => {
                replaceSearchIcon()
            }, 5)
        })
    }

    const replaceSearchIcon = (): void => {
        const searchEmoji: HTMLSpanElement | null = document.querySelector(`.${containerClass} .search-emoji`)
        if (searchEmoji) {
            const parser = new DOMParser()
            const svgDoc: Document = parser.parseFromString(SVG_ICONS.SEARCH, 'image/svg+xml')
            const svgElement = svgDoc.documentElement
            searchEmoji.appendChild(svgElement)
        }
    }

    return (
        <div
            className={`flex items-center border ${classes} ${containerClass} ${className} ${
                readOnly ? 'read-only' : ''
            }`}
            date-test="input-phone"
        >
            <ReactPhoneInput
                {...rest}
                country={country}
                preferredCountries={preferredCountries}
                /**
                 * Known reactivity issue: https://github.com/bl00mber/react-phone-input-2/issues/631
                 */
                localization={htmlLocale === 'nl' ? nl : undefined}
                enableSearch
                searchPlaceholder={searchPlaceholder}
                searchNotFound={searchNotFound}
                placeholder={CountryCode.NL}
                onFocus={onFocusHandler}
                onBlur={onBlurHandler}
                inputProps={{ id, autoFocus: autoFocus }}
                disabled={rest.disabled || readOnly}
            />
        </div>
    )
}
